import { useEffect, useMemo, useState } from 'react';
import { getUserProfile, getUserProfileByEmail } from '../services/firebase/auth';

interface ProfileSummary {
  displayName?: string;
  avatarUrl?: string;
}

const profileCache = new Map<string, ProfileSummary | undefined>();

/**
 * Resolves profile summaries for a list of user IDs.
 * Returns a map keyed by userId with displayName/avatarUrl when available.
 */
export function useUserProfiles(userIds: string[]): Record<string, ProfileSummary | undefined> {
  const [profiles, setProfiles] = useState<Record<string, ProfileSummary | undefined>>({});

  const lookup = useMemo(() => {
    const normalizedByKey = new Map<string, string>();
    const normalizedSet = new Set<string>();
    for (const id of userIds) {
      const normalized = normalizeProfileKey(id);
      if (!normalized) {
        continue;
      }
      normalizedByKey.set(id, normalized);
      normalizedSet.add(normalized);
    }
    return { normalizedByKey, normalizedKeys: Array.from(normalizedSet) };
  }, [userIds]);

  useEffect(() => {
    let cancelled = false;

    async function loadProfiles(): Promise<void> {
      const idsToFetch = lookup.normalizedKeys.filter(
        (id) => shouldFetch(id) && !profileCache.has(id)
      );
      if (idsToFetch.length === 0) {
        if (!cancelled) {
          setProfiles(buildProfileMap(userIds, lookup.normalizedByKey));
        }
        return;
      }

      await Promise.all(
        idsToFetch.map(async (id) => {
          try {
            const profile = await resolveProfile(id);
            if (profile) {
              profileCache.set(id, {
                displayName: profile.displayName,
                avatarUrl: profile.avatarUrl || undefined,
              });
            } else {
              profileCache.set(id, undefined);
            }
          } catch {
            profileCache.set(id, undefined);
          }
        })
      );

      if (!cancelled) {
        setProfiles(buildProfileMap(userIds, lookup.normalizedByKey));
      }
    }

    loadProfiles();
    return () => {
      cancelled = true;
    };
  }, [lookup, userIds]);

  return profiles;
}

function buildProfileMap(
  userIds: string[],
  normalizedByKey: Map<string, string>
): Record<string, ProfileSummary | undefined> {
  const map: Record<string, ProfileSummary | undefined> = {};
  for (const id of userIds) {
    const normalized = normalizedByKey.get(id);
    map[id] = normalized ? profileCache.get(normalized) : undefined;
  }
  return map;
}

function shouldFetch(userId: string): boolean {
  if (!userId) {
    return false;
  }

  if (isEmailKey(userId)) {
    return true;
  }

  if (userId.startsWith('invited-')) {
    return false;
  }

  if (userId.startsWith('web-')) {
    return false;
  }

  return true;
}

async function resolveProfile(userId: string) {
  if (isEmailKey(userId)) {
    return await getUserProfileByEmail(userId);
  }

  return await getUserProfile(userId);
}

function isEmailKey(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
}

function normalizeProfileKey(value: string): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (isEmailKey(trimmed)) {
    return trimmed.toLowerCase();
  }

  return trimmed;
}
